import { Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import './App.scss'
import { LoadingPage } from './common'
import { ScrollToTop } from './components/ScrollToTop'

import { AccessControl, AppLayout } from '@components/layout'
import { ApplicationsDetail } from '@features/applications/views/admin/ApplicationsDetail.tsx'

import { ProtectedRoute } from '@features/auth/components'
import { useAuthentication } from '@features/auth/context'
import {
  CreatePassword,
  ForgottenPassword,
  Login,
  Register
} from '@features/auth/views'
import { AdminChat } from '@features/chat/views/admin/AdminChat.tsx'
import { Contract } from '@features/contracts'
import { Dashboard } from '@features/dashboard'
import { LandlordDashboard } from '@features/dashboard/views/landlord/LandlordDashboard'
import { UserDashboard } from '@features/dashboard/views/user/UserDashboard'
import { LandlordFlats } from '@features/flats/views/landlord/LandlordFlats'
import { LandlordFlatsDetail } from '@features/flats/views/landlord/LandlordFlatsDetail'
import { LandlordPayments } from '@features/payments/views/landlord/LandlordPayments'
import { Flats } from '@features/flats/views/admin/Flats'
import { FlatsCreate } from '@features/flats/views/admin/FlatsCreate'
import { FlatsDetail } from '@features/flats/views/admin/FlatsDetail'
import {
  Application,
  ApplicationInvitation,
  Applications,
  Users
} from '@features/index'
import { Landlords } from '@features/landlords/views/admin/Landlords'
import { LandlordsDetail } from '@features/landlords/views/admin/LandlordsDetail'
import { OfferCreate } from '@features/offers/views/admin/OfferCreate'
import { OfferEdit } from '@features/offers/views/admin/OfferEdit'
import { Offers } from '@features/offers/views/admin/Offers'
import { OfferDetail } from '@features/offers/views/user/OfferDetail'
import { OfferView } from '@features/offers/views/user/OfferView'
import { Partners, ReferralRedirect } from '@features/referral'
import { BankNotifications } from '@features/banking/views/admin/BankNotifications'
import { CronJobs } from '@features/cron/views/admin/CronJobs'
import { CronJobsDetail } from '@features/cron/views/admin/CronJobsDetail'
import { EventAnalyticsDashboard } from '@features/event-analytics/views/EventAnalyticsDashboard'
import { SendEmail } from '@features/sendEmail/views/admin/SendEmail'
import { ReferralsPage } from '@features/settings/views/user/ReferralsPage'
import { Settings } from '@features/settings/views/user/Settings'
import { UsersDetail } from '@features/users/views/admin/UsersDetail.tsx'
import { User } from '@features/users/views/user/User'
import { TicketsDetail } from '@features/tickets/views/admin/TicketsDetail'
import { TicketsTable } from '@features/tickets/views/admin/TicketsTable'
import { TicketDetail } from '@features/tickets/views/user/TicketDetail'
import { Tickets } from '@features/tickets/views/user/Tickets'
import { SettlementDetail } from '@features/settlements/views/admin/SettlementDetail'
import { UserSettlementDetail } from '@features/settlements/views/user/UserSettlementDetail'
import { Welcome } from '@features/welcome/Welcome'
import {
  AiSearch,
  ApartmentDetail,
  Apartments,
  ApartmentsMap
} from '@features/livability'
import { LandlordsCreate } from './features/landlords/views/admin/LandlordsCreate'

function App() {
  const { role } = useAuthentication()
  return (
    <Suspense fallback={<LoadingPage />}>
      <ScrollToTop />
      <Routes>
        <Route path="/ref" element={<ReferralRedirect />} />

        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/create-new-password" element={<CreatePassword />} />
        <Route path="/forgotten-password" element={<ForgottenPassword />} />
        <Route path="/create-password" element={<CreatePassword />} />

        <Route index element={fallback(role)} />

        <Route element={<ProtectedRoute />}>
          <Route path="/user" element={<AppLayout />}>
            <Route index element={<Navigate to={'dashboard'} replace />} />
            <Route path="dashboard" element={<UserDashboard />} />
            <Route path="welcome" element={<Welcome />} />
            <Route path="application" element={<Application />} />
            <Route
              path="application/invitation/:id"
              element={<ApplicationInvitation />}
            />
            <Route path="profile" element={<User />} />
            <Route path="offer" element={<OfferView />} />
            <Route path="offer/:id" element={<OfferDetail />} />
            <Route path="contract" element={<Contract />} />
            <Route path="tickets" element={<Tickets />} />
            <Route path="tickets/:id" element={<TicketDetail />} />
            {/* Settlements live inside the Nájom page; the old standalone
                list URL keeps working. */}
            <Route
              path="settlements"
              element={
                <Navigate to="/user/contract?tab=settlements" replace />
              }
            />
            <Route path="settlements/:id" element={<UserSettlementDetail />} />
            <Route path="referrals" element={<ReferralsPage />} />
            <Route path="settings" element={<Settings />} />
            <Route path="apartments-search" element={<AiSearch />} />
            <Route path="apartments" element={<Apartments />} />
            <Route path="apartments-map" element={<ApartmentsMap />} />
            <Route path="apartments/:id" element={<ApartmentDetail />} />
          </Route>
        </Route>
        <Route element={<ProtectedRoute />}>
          <Route
            path="/admin"
            element={
              <AccessControl role="ADMIN">
                <AppLayout />
              </AccessControl>
            }
          >
            <Route index element={<Navigate to={'dashboard'} replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="applications" element={<Applications />} />
            <Route path="applications/:id" element={<ApplicationsDetail />} />
            <Route path="users" element={<Users />} />
            <Route path="users/:id" element={<UsersDetail />} />

            <Route path="flats" element={<Flats />} />
            <Route path="flats/:id" element={<FlatsDetail />} />
            <Route path="flats/create" element={<FlatsCreate />} />

            <Route path="settlements/:id" element={<SettlementDetail />} />

            {/* <Route path="draft-flats" element={<DraftFlats />} /> */}

            <Route path="tickets" element={<TicketsTable />} />
            <Route path="tickets/:id" element={<TicketsDetail />} />

            <Route path="offers" element={<Offers />} />
            <Route path="offers/create" element={<OfferCreate />} />
            <Route path="offers/:id" element={<OfferEdit />} />

            <Route path="/admin/landlords" element={<Landlords />} />
            <Route
              path="/admin/landlords/create"
              element={<LandlordsCreate />}
            />
            <Route path="/admin/landlords/:id" element={<LandlordsDetail />} />
            <Route path="/admin/chat" element={<AdminChat />} />

            <Route path="/admin/partners" element={<Partners />} />

            <Route path="send-email" element={<SendEmail />} />
            <Route path="bank-notifications" element={<BankNotifications />} />
            <Route path="cron-jobs" element={<CronJobs />} />
            <Route path="cron-jobs/:id" element={<CronJobsDetail />} />
            <Route path="event-analytics" element={<EventAnalyticsDashboard />} />
            <Route path="apartments-search" element={<AiSearch />} />
            <Route path="apartments" element={<Apartments />} />
            <Route path="apartments-map" element={<ApartmentsMap />} />
            <Route path="apartments/:id" element={<ApartmentDetail />} />
          </Route>
        </Route>
        <Route element={<ProtectedRoute />}>
          <Route
            path="/landlord"
            element={
              <AccessControl role="LANDLORD">
                <AppLayout />
              </AccessControl>
            }
          >
            <Route index element={<Navigate to={'dashboard'} replace />} />
            <Route path="dashboard" element={<LandlordDashboard />} />
            <Route path="flats" element={<LandlordFlats />} />
            <Route path="flats/:id" element={<LandlordFlatsDetail />} />
            <Route path="payments" element={<LandlordPayments />} />
            <Route path="profile" element={<User />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}

export default App

function fallback(role?: string) {
  if (!role) {
    return <Navigate to={'/login'} replace />
  }
  if (role === 'USER' || role === 'ROOMMATE') {
    return <Navigate to={'/user/dashboard'} replace />
  }
  if (role === 'ADMIN') {
    return <Navigate to={'/admin/dashboard'} replace />
  }
  if (role === 'LANDLORD') {
    return <Navigate to={'/landlord/dashboard'} replace />
  }
  return <Navigate to={'/login'} replace />
}
